import * as React from "react";
import { Sparkles } from "lucide-react";
import { FormattedNumber } from "react-intl";
import { Link, useNavigate } from "react-router-dom";
import type { Requisition as ApiRequisition } from "randmar-api-client";
import {
  useGetV4WarehousesQuery,
  usePutV4ManufacturerByRouteManufacturerIdPurchaseOrderMutation,
} from "randmar-api-client";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useChat,
  useToast,
} from "randmarcomps";
import { useManufacturer } from "../../contexts/WithManufacturer";
import Path from "../../routes/Path";
import { RootStoreContext } from "../../stores/rootStore";
import CancelRequisition from "../document/actions/CancelRequisition";
import FormatDateFromInt from "../util/DateTimeUtil";
import { TablePaginationBar } from "../util/TablePaginationBar";

type CurrencyFormatProps = Omit<React.ComponentProps<typeof FormattedNumber>, "value" | "children">;

interface ManufacturerRequisitionsGridProps {
  requisitions: ApiRequisition[];
  currencyFormat: CurrencyFormatProps;
  defaultMinimumOrder?: number | null;
  defaultMaxInventoryValue?: number | null;
  onRequisitionsChanged?: () => void;
}

type DraftRow = {
  requisitionNumber: string;
  location: string;
  locationLabel: string;
  requisitionDate?: number;
  subTotal: number;
  requestedByLabel: string;
};

const rowsPerPageOptions = [10, 20, 50, 100] as const;

const ManufacturerRequisitionsGrid: React.FC<ManufacturerRequisitionsGridProps> = ({
  requisitions,
  currencyFormat,
  defaultMinimumOrder,
  defaultMaxInventoryValue,
  onRequisitionsChanged,
}) => {
  const { applicationId } = useManufacturer();
  const navigate = useNavigate();
  const { sendMessageWithClear } = useChat();
  const { toast } = useToast();
  const { userStore } = React.useContext(RootStoreContext);

  const [checkedDrafts, setCheckedDrafts] = React.useState<string[]>([]);
  const [sendToManufacturer, setSendToManufacturer] = React.useState(true);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState<(typeof rowsPerPageOptions)[number]>(20);

  const isSuperAdmin = userStore.isSuperAdmin();

  const { data: warehouses } = useGetV4WarehousesQuery();
  const [createPurchaseOrder, createPurchaseOrderState] =
    usePutV4ManufacturerByRouteManufacturerIdPurchaseOrderMutation();

  const getWarehouseNameFromCode = React.useCallback(
    (code: string | undefined | null): string => {
      if (!code) {
        return "B/O";
      }
      if (!warehouses) {
        return code;
      }
      const warehouse = warehouses.find((w) => w.WarehouseId === code);
      return warehouse?.Name || code;
    },
    [warehouses]
  );

  const draftRows = React.useMemo<DraftRow[]>(() => {
    const normalizeRequestedBy = (requestedBy: string, reference: string) => {
      if (requestedBy === "Echo") return "Classic Algorithm";
      if (reference === "AI Generated") return "AI Algorithm";
      return requestedBy;
    };

    const rows: DraftRow[] = [];
    for (const requisition of requisitions) {
      const requisitionNumber = requisition.RequisitionNumber ?? "";
      if (!requisitionNumber) {
        continue;
      }

      const location = requisition.Location?.trim() || "Unassigned";
      const reference = requisition.Reference?.trim() || "No reference";
      const requestedBy = requisition.RequestedBy?.trim() || "Unknown";

      rows.push({
        requisitionNumber,
        location,
        locationLabel: getWarehouseNameFromCode(requisition.Location),
        requisitionDate: requisition.RequisitionDate ?? undefined,
        subTotal: requisition.SubTotal ?? 0,
        requestedByLabel: normalizeRequestedBy(requestedBy, reference),
      });
    }

    return rows.sort((a, b) => {
      const locationCompare = a.location.localeCompare(b.location);
      if (locationCompare !== 0) return locationCompare;
      const dateCompare = (b.requisitionDate ?? 0) - (a.requisitionDate ?? 0);
      if (dateCompare !== 0) return dateCompare;
      const requesterCompare = a.requestedByLabel.localeCompare(b.requestedByLabel);
      if (requesterCompare !== 0) return requesterCompare;
      return a.requisitionNumber.localeCompare(b.requisitionNumber);
    });
  }, [getWarehouseNameFromCode, requisitions]);

  const selectedLocation = React.useMemo(() => {
    if (!checkedDrafts.length) {
      return null;
    }
    const first = draftRows.find((row) => row.requisitionNumber === checkedDrafts[0]);
    return first?.location ?? null;
  }, [checkedDrafts, draftRows]);

  const selectedTotal = React.useMemo(
    () =>
      checkedDrafts.reduce((sum, requisitionNumber) => {
        const row = draftRows.find((draft) => draft.requisitionNumber === requisitionNumber);
        return sum + (row?.subTotal ?? 0);
      }, 0),
    [checkedDrafts, draftRows]
  );

  const minimumOrderThreshold = Math.max(0, defaultMinimumOrder ?? 0);
  const isMinimumOrderMet = minimumOrderThreshold <= 0 || selectedTotal >= minimumOrderThreshold;

  const canGeneratePurchaseOrder =
    checkedDrafts.length > 0 && (isMinimumOrderMet || isSuperAdmin) && !createPurchaseOrderState.isLoading;

  const handleAssistantPrompt = React.useCallback(() => {
    sendMessageWithClear("Please guide me to create a draft purchase order");
  }, [sendMessageWithClear]);

  const handleCheckedChange = React.useCallback((requisitionNumber: string, isChecked: boolean) => {
    setCheckedDrafts((prev) => {
      if (isChecked) {
        if (prev.includes(requisitionNumber)) {
          return prev;
        }
        return [...prev, requisitionNumber];
      }
      return prev.filter((value) => value !== requisitionNumber);
    });
  }, []);

  const handleConfirmCreatePurchaseOrder = React.useCallback(async () => {
    if (!applicationId || checkedDrafts.length === 0) {
      return;
    }

    try {
      const purchaseOrderNumber = await createPurchaseOrder({
        routeManufacturerId: applicationId,
        sendToManufacturer,
        body: checkedDrafts,
      }).unwrap();

      toast({
        variant: "success",
        title: "Purchase order created",
        description: sendToManufacturer
          ? "The PO was generated and sent to the manufacturer."
          : "The PO was generated and saved (not sent).",
      });

      setConfirmOpen(false);
      setCheckedDrafts([]);

      onRequisitionsChanged?.();

      if (purchaseOrderNumber?.startsWith("PO")) {
        navigate(Path.document(applicationId, purchaseOrderNumber));
      }
    } catch (error) {
      console.error(error);
      toast({
        variant: "destructive",
        title: "Could not create purchase order",
        description: "Double-check your selection and try again.",
      });
      setConfirmOpen(false);
    }
  }, [applicationId, checkedDrafts, createPurchaseOrder, navigate, onRequisitionsChanged, sendToManufacturer, toast]);

  if (requisitions.length === 0) {
    return (
      <Card className="rounded-2xl border border-dashed border-gray-200 bg-white shadow-sm">
        <CardHeader className="items-center space-y-3 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-[var(--ops-accent-muted)] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[var(--ops-accent)]">
            <Sparkles className="h-4 w-4" aria-hidden="true" /> No drafts yet
          </div>
          <CardTitle className="text-2xl font-semibold text-slate-900">Create your first draft purchase</CardTitle>
          <CardDescription className="text-[15px] text-slate-600">
            Add high-moving items to a draft PO. As soon as you create one, it appears here.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-4 text-center">
          <Button onClick={handleAssistantPrompt} size="lg" className="w-full max-w-xs">
            <Sparkles className="mr-2 h-4 w-4" aria-hidden="true" />
            Open Assistant
          </Button>
          <p className="text-xs text-slate-500">You can fine-tune automation and qualification later.</p>
        </CardContent>
      </Card>
    );
  }

  const tableCaptionId = "draft-purchases-table-caption";

  return (
    <>
      <div className="overflow-x-auto rounded-2xl border border-gray-100">
        <Table>
          <caption id={tableCaptionId} className="sr-only">
            Select draft purchases that share a location to convert them into a purchase order.
          </caption>
          <TableHeader className="bg-gray-50">
            <TableRow className="h-14 hover:bg-gray-50">
              <TableHead className="w-10" aria-label="Select drafts" />
              <TableHead className="min-w-[170px] text-xs font-semibold uppercase tracking-wide text-slate-500">
                Draft number
              </TableHead>
              <TableHead className="min-w-[200px] text-xs font-semibold uppercase tracking-wide text-slate-500">
                Location
              </TableHead>
              <TableHead className="min-w-[160px] text-xs font-semibold uppercase tracking-wide text-slate-500">
                Draft date
              </TableHead>
              <TableHead className="min-w-[160px] text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                Subtotal
              </TableHead>
              <TableHead className="min-w-[180px] text-xs font-semibold uppercase tracking-wide text-slate-500">
                Requestor
              </TableHead>
              <TableHead className="w-12" aria-label="Actions" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {draftRows
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row) => {
                const isChecked = checkedDrafts.includes(row.requisitionNumber);
                const isLocked = Boolean(selectedLocation && row.location !== selectedLocation);

                return (
                  <TableRow
                    key={row.requisitionNumber}
                    className="h-14 border-b border-gray-100 hover:bg-gray-50/60"
                  >
                    <TableCell>
                      <Checkbox
                        checked={isChecked}
                        disabled={isLocked}
                        onCheckedChange={(nextValue) => handleCheckedChange(row.requisitionNumber, Boolean(nextValue))}
                        aria-label={`Select draft purchase ${row.requisitionNumber}`}
                      />
                    </TableCell>
                    <TableCell className="font-semibold">
                      <Link
                        to={Path.document(applicationId, row.requisitionNumber)}
                        className="text-slate-900 underline-offset-4 hover:text-[color:var(--ops-accent)] hover:underline"
                      >
                        {row.requisitionNumber}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <span
                        className={cx(
                          "inline-flex items-center rounded-full border px-3 py-1 text-[13px] font-medium text-slate-700",
                          selectedLocation && row.location === selectedLocation
                            ? "border-dashed border-[color:var(--ops-accent)] bg-[var(--ops-accent-muted)] text-[color:var(--ops-accent)]"
                            : "border-gray-200 bg-white"
                        )}
                        title={isLocked ? "Select drafts from the same location to bundle into one PO." : undefined}
                      >
                        {row.locationLabel}
                      </span>
                    </TableCell>
                    <TableCell className="text-slate-700">
                      {FormatDateFromInt(row.requisitionDate)}
                    </TableCell>
                    <TableCell className="text-right font-medium text-slate-900">
                      <FormattedNumber value={row.subTotal} {...currencyFormat} />
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" size="sm">
                        {row.requestedByLabel}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <CancelRequisition
                        requisitionNumber={row.requisitionNumber}
                        fab
                        withReload
                        onSuccess={onRequisitionsChanged}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </div>

      <div className="mt-4 flex flex-col gap-4 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm md:flex-row md:items-center md:justify-between">
        <div className="space-y-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Selected total</p>
          <p className="text-xl font-semibold text-slate-900">
            <FormattedNumber value={selectedTotal} {...currencyFormat} />
          </p>
          <p className="text-[13px] text-slate-500">
            {checkedDrafts.length
              ? `${checkedDrafts.length} draft${checkedDrafts.length === 1 ? "" : "s"} selected`
              : "Select one or more drafts to generate a PO."}
          </p>
          <p className="text-[13px] text-slate-500">
            Max inventory value:{" "}
            <span className="font-medium text-slate-700">
              {defaultMaxInventoryValue !== null && defaultMaxInventoryValue !== undefined
                ? <FormattedNumber value={defaultMaxInventoryValue} {...currencyFormat} />
                : "Not set"}
            </span>
          </p>
          {!isMinimumOrderMet && checkedDrafts.length > 0 ? (
            <div className="mt-2 inline-flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-900">
              <span className="text-xs font-semibold uppercase tracking-wide">Minimum not met</span>
              <span>
                Missing{" "}
                <FormattedNumber
                  value={Math.max(0, minimumOrderThreshold - selectedTotal)}
                  {...currencyFormat}
                />
                .
              </span>
              {isSuperAdmin ? <span className="font-semibold">Super admin override enabled.</span> : null}
            </div>
          ) : null}
        </div>

        <Button
          type="button"
          onClick={() => setConfirmOpen(true)}
          disabled={!canGeneratePurchaseOrder}
          variant={!isMinimumOrderMet && isSuperAdmin ? "destructive" : "default"}
          className="w-full rounded-xl md:w-auto"
        >
          Generate Purchase Order
        </Button>
      </div>

      {draftRows.length > rowsPerPage ? (
        <div className="mt-4">
          <TablePaginationBar
            page={page}
            rowsPerPage={rowsPerPage}
            totalRows={draftRows.length}
            rowsPerPageOptions={rowsPerPageOptions}
            onPageChange={(nextPage) => setPage(nextPage)}
            onRowsPerPageChange={(nextRows) => {
              setRowsPerPage(nextRows as (typeof rowsPerPageOptions)[number]);
              setPage(0);
            }}
          />
        </div>
      ) : null}

      <Dialog open={confirmOpen} onOpenChange={(isOpen) => !createPurchaseOrderState.isLoading && setConfirmOpen(isOpen)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm purchase order creation</DialogTitle>
            <DialogDescription>
              This will generate a purchase order from the selected drafts. Drafts must share a location.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total</p>
                <p className="text-[15px] font-semibold text-slate-900">
                  <FormattedNumber value={selectedTotal} {...currencyFormat} />
                </p>
              </div>
              <Badge variant="secondary" size="sm">
                {checkedDrafts.length} selected
              </Badge>
            </div>

            <div className="flex items-start justify-between gap-4 rounded-xl border border-gray-100 bg-white px-4 py-3">
              <div className="space-y-1">
                <Label htmlFor="send-po-switch" className="text-[15px] font-semibold text-slate-900">
                  Send purchase order to manufacturer
                </Label>
                <p className="text-[13px] text-slate-500">
                  If enabled, Randmar will notify the manufacturer immediately after generating the PO.
                </p>
              </div>
              <Switch
                id="send-po-switch"
                checked={sendToManufacturer}
                onCheckedChange={(checked) => setSendToManufacturer(Boolean(checked))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirmOpen(false)} disabled={createPurchaseOrderState.isLoading}>
              Cancel
            </Button>
            <Button type="button" onClick={handleConfirmCreatePurchaseOrder} disabled={!canGeneratePurchaseOrder}>
              {createPurchaseOrderState.isLoading ? "Generating…" : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

function cx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

export default ManufacturerRequisitionsGrid;
